import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LayoutDashboard, CalendarRange, Lock, LogOut, ShieldCheck } from "lucide-react";
import BrandLogo from "../../components/BrandLogo";

const menu = [
    { to: "/manager", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "mgr-nav-dashboard" },
    { to: "/manager/bookings", label: "Bookings", icon: CalendarRange, testid: "mgr-nav-bookings" },
    { to: "/manager/slots", label: "Slots", icon: Lock, testid: "mgr-nav-slots" },
];

export default function ManagerLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => { await logout(); navigate("/login"); };

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar (desktop) */}
            <aside className="hidden md:flex w-64 flex-col bg-slate-950 border-r border-slate-800">
                <div className="p-5 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <BrandLogo size="md" />
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66]">Manager</div>
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {menu.map(({ to, label, icon: Icon, end, testid }) => (
                        <NavLink key={to} to={to} end={end} data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 h-10 rounded text-sm font-semibold transition-colors ${
                                    isActive ? "bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/30" : "text-slate-400 hover:text-white hover:bg-slate-900"
                                }`
                            }>
                            <Icon className="w-4 h-4" /> {label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-800 space-y-2">
                    <div className="px-2 py-2 bg-slate-900 border border-slate-800 rounded">
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Logged in as</div>
                        <div className="text-sm font-bold text-white truncate" data-testid="mgr-username">{user?.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#00FF66] inline-flex items-center gap-1 mt-0.5">
                            <ShieldCheck className="w-3 h-3" /> Manager
                        </div>
                    </div>
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 h-10 rounded text-sm text-slate-400 hover:text-red-400 hover:bg-slate-900" data-testid="mgr-logout-btn">
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800 h-14 flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <BrandLogo size="sm" />
                    <span className="font-display font-bold uppercase tracking-wider text-[#00FF66] text-[10px]">Manager</span>
                </div>
                <button onClick={handleLogout} className="text-slate-400" data-testid="mgr-mobile-logout">
                    <LogOut className="w-4 h-4" />
                </button>
            </div>

            {/* Mobile bottom nav */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-950/90 backdrop-blur-xl border-t border-slate-800">
                <ul className="grid grid-cols-3 h-16">
                    {menu.map(({ to, label, icon: Icon, end, testid }) => (
                        <li key={to}>
                            <NavLink to={to} end={end} data-testid={`m-${testid}`}
                                className={({ isActive }) =>
                                    `flex flex-col items-center justify-center h-full gap-1 ${isActive ? "text-[#00FF66]" : "text-slate-400"}`
                                }>
                                <Icon className="w-5 h-5" />
                                <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>

            <main className="flex-1 pt-14 md:pt-0 pb-20 md:pb-0 page-fade-in">{children}</main>
        </div>
    );
}
